import { getMetafield } from 'src/functions/util/getMetafield'

import type { Product, ProductOption, ProductOptionValue, ProductVariant } from 'src/data/graphql-generated'

type ProductOptionName = ProductOption['name']

export const isOptionActive = (
  variant: ProductVariant,
  optionName: ProductOptionName,
  optionValue: ProductOptionValue,
): boolean => variant.selectedOptions.some((option) => option.name === optionName && option.value === optionValue.name)

export const findRelatedVariantByOption = (
  product: Product,
  variant: ProductVariant,
  optionName: ProductOptionName,
  optionValue: ProductOptionValue,
): ProductVariant | undefined => {
  const targetOptions = variant.selectedOptions.map((option) =>
    option.name === optionName ? { name: optionName, value: optionValue.name } : option,
  )
  const targetVariant = product.variants.nodes.find(({ selectedOptions }) =>
    selectedOptions.every(({ name, value }, i) => name === targetOptions[i].name && value === targetOptions[i].value),
  )
  return targetVariant
}

export const findAnyVariantByOption = (
  product: Product,
  optionName: ProductOptionName,
  optionValue: ProductOptionValue,
): ProductVariant | undefined => {
  const matchingVariants = product.variants.nodes.filter(({ selectedOptions }) =>
    selectedOptions.some(({ name, value }) => name === optionName && value === optionValue.name),
  )
  return matchingVariants[0]
}

export const findVariantByOption = (
  product: Product,
  variant: ProductVariant,
  optionName: ProductOptionName,
  optionValue: ProductOptionValue,
): ProductVariant => {
  const adjacentVariant = findRelatedVariantByOption(product, variant, optionName, optionValue)
  if (adjacentVariant) return adjacentVariant

  const anyMatchingVariant = findAnyVariantByOption(product, optionName, optionValue)
  if (anyMatchingVariant) return anyMatchingVariant

  return product.variants.nodes[0]
}

export const isOptionAvailable = (
  product: Product,
  variant: ProductVariant,
  optionName: ProductOptionName,
  optionValue: ProductOptionValue,
): boolean => {
  const targetVariant = findVariantByOption(product, variant, optionName, optionValue)
  return !!targetVariant && !getMetafield(targetVariant.metafields || [], 'product', 'archived')
}

export const getAvailableOptions = (product: Product, variant: ProductVariant): ProductOption[] =>
  product.options
    .map((optionGroup) => ({
      ...optionGroup,
      optionValues: optionGroup.optionValues.filter((value) =>
        isOptionAvailable(product, variant, optionGroup.name, value),
      ),
    }))
    .filter((optionGroup) => optionGroup.optionValues.length > 0)
